import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-muted">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Legal
            </p>
            <h1 className="heading-xl mb-6">
              Terms & <em className="italic">Conditions</em>
            </h1>
            <p className="text-sm text-muted-foreground">Last updated: April 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing">
        <div className="container-custom max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-10"
          >
            {/* 1. About */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">1. About Sacred Sounds Studio</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Sacred Sounds Studio provides holistic wellness services including sound healing massage, 
                Reiki, therapeutic massage, sound bath experiences, and group Sway with Sound classes. 
                By booking a session or purchasing a product through our website, you agree to the 
                following terms and conditions.
              </p>
            </div>

            {/* 2. Bookings */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">2. Bookings & Payments</h2>
              <ul className="text-sm text-muted-foreground leading-relaxed space-y-2">
                <li>• All bookings require a £20 deposit at the time of booking to secure your appointment.</li>
                <li>• The deposit is deducted from the total treatment price. The remaining balance is payable on arrival by cash or bank transfer.</li>
                <li>• Deposits are processed securely through Stripe. We do not store your card details.</li>
                <li>• A confirmation email will be sent to the email address provided upon successful payment, showing the deposit paid and remaining balance.</li>
                <li>• Please ensure all booking details (name, email, date, time) are correct before completing payment.</li>
                <li>• Bookings are subject to availability.</li>
              </ul>
            </div>

            {/* 3. Cancellation */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">3. Cancellation & Refund Policy</h2>
              <ul className="text-sm text-muted-foreground leading-relaxed space-y-2">
                <li>• <strong>Cancellation with 48+ hours' notice:</strong> £20 deposit can be refunded or transferred to a new date.</li>
                <li>• <strong>Cancellation with less than 48 hours' notice:</strong> £20 deposit is non-refundable.</li>
                <li>• <strong>Rescheduling with 48+ hours' notice:</strong> £20 deposit can be transferred to a new date.</li>
                <li>• <strong>No-show:</strong> £20 deposit is non-refundable.</li>
                <li>• To cancel or reschedule, please contact us at <a href="mailto:hello@soundhealingmassage.com" className="text-primary underline">hello@soundhealingmassage.com</a>.</li>
              </ul>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                For full details, please see our <Link to="/cancellation-policy" className="text-primary underline">Cancellation Policy</Link>.
              </p>
            </div>

            {/* 4. Late Arrivals */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">4. Late Arrivals</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If you arrive late, your session may need to be shortened to avoid disrupting 
                the next appointment. The full session fee will still apply. We recommend arriving 
                5–10 minutes early so you can settle in and relax before your session begins.
              </p>
            </div>

            {/* 5. Health */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">5. Health & Wellbeing</h2>
              <ul className="text-sm text-muted-foreground leading-relaxed space-y-2">
                <li>• Our services are holistic and complementary in nature and are not a substitute for professional medical advice, diagnosis, or treatment.</li>
                <li>• Please inform us of any medical conditions, injuries, allergies, or if you are pregnant before your session.</li>
                <li>• We reserve the right to refuse or modify a treatment if we believe it may not be suitable for you on the day.</li>
                <li>• By booking, you confirm that you have disclosed any relevant health information and accept full responsibility for your decision to receive treatment.</li>
              </ul>
            </div>

            {/* 6. Gift Vouchers */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">6. Gift Vouchers</h2>
              <ul className="text-sm text-muted-foreground leading-relaxed space-y-2">
                <li>• Gift vouchers are valid for 12 months from the date of purchase unless otherwise stated.</li>
                <li>• Gift vouchers are non-refundable and cannot be exchanged for cash.</li>
                <li>• Gift vouchers are transferable and can be used by anyone the purchaser chooses.</li>
                <li>• Lost or stolen vouchers cannot be replaced.</li>
              </ul>
            </div>

            {/* 7. Group Classes */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">7. Group Classes (Sway with Sound)</h2>
              <ul className="text-sm text-muted-foreground leading-relaxed space-y-2">
                <li>• Places are limited and allocated on a first-come, first-served basis.</li>
                <li>• Please bring your own blanket and pillow for comfort during the session.</li>
                <li>• The same cancellation policy applies to group classes.</li>
                <li>• We reserve the right to cancel or reschedule a class due to unforeseen circumstances, in which case a full refund or alternative date will be offered.</li>
              </ul>
            </div>

            {/* 8. Location */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">8. Location & Venue</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our studio is located in Barking, Essex, UK. The full address and directions 
                will be shared via email 24 hours before your appointment. Please ensure you 
                check your email (including spam/junk folders) for this information.
              </p>
            </div>

            {/* 9. Privacy */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">9. Privacy & Data</h2>
              <ul className="text-sm text-muted-foreground leading-relaxed space-y-2">
                <li>• We collect personal information (name, email, phone number) solely for the purpose of managing your booking and communicating with you about your appointment.</li>
                <li>• We will never sell or share your personal data with third parties for marketing purposes.</li>
                <li>• If you subscribe to our newsletter, you can unsubscribe at any time.</li>
                <li>• Payment information is handled securely by Stripe and is never stored on our servers.</li>
              </ul>
            </div>

            {/* 10. Liability */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">10. Liability</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Sacred Sounds Studio takes every care to ensure a safe and welcoming environment. 
                However, we accept no liability for any loss, damage, or injury sustained during 
                or as a result of any treatment or class, except where caused by our negligence. 
                By attending a session, you acknowledge and accept this.
              </p>
            </div>

            {/* 11. Changes */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">11. Changes to These Terms</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We reserve the right to update these terms and conditions at any time. 
                Any changes will be reflected on this page with an updated date. 
                Continued use of our services constitutes acceptance of the updated terms.
              </p>
            </div>

            {/* Contact */}
            <div className="pt-6 border-t border-border">
              <h2 className="font-serif text-xl text-foreground mb-3">Questions?</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                If you have any questions about these terms, please don't hesitate to get in touch.
              </p>
              <a
                href="mailto:hello@soundhealingmassage.com"
                className="inline-flex items-center gap-2 text-primary text-sm hover:underline"
              >
                <Mail className="w-4 h-4" />
                hello@soundhealingmassage.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
